<template>
	<section
		class="lg:px-30 bg-headerBg tall:lg:pt-10 relative w-full px-4 pb-6"
		:class="{ 'tall:lg:pb-10': !isSuccessScreen, 'tall:lg:pb-28 lg:pb-24': isSuccessScreen }"
	>
		<div
			v-if="dialogs.headerFormModalOpen"
			class="fixed top-0 left-0 z-[3] h-full w-full bg-black/50"
		></div>

		<div class="mb-4 flex items-center justify-between pt-4">
			<div class="w-7" />

			<div class="w-16 lg:hidden">
				<LogoPower class="mx-auto h-8 w-12 sm:h-9 lg:w-16" clickable white />
			</div>

			<CloseSvg
				class="z-2 h-7 w-7 rotate-90 cursor-pointer fill-gray-400 hover:fill-gray-300 lg:absolute lg:top-5 lg:right-5 lg:h-8 lg:w-8"
				@click="dialogs.vehicleOpen = false"
			/>
		</div>

		<div class="container mx-auto flex w-full flex-col px-4 lg:px-20">
			<div
				class="text-headerTitle xs:text-26 tall:md:text-36 tall:xl:text-4xl xl:text-38 relative order-2 flex min-h-[3em] w-full flex-col items-center justify-center text-center text-2xl font-bold md:text-3xl lg:order-1 lg:min-h-[auto] lg:pb-4 lg:text-3xl"
			>
				<img
					v-if="isWarningVisible"
					class="mx-auto mb-1 h-32 w-32"
					:src="getAssetUrl('/assets/warning-raw.svg')"
					alt="Warning"
				/>
				{{ headerFormTitle() }}
			</div>

			<div class="order-1 lg:order-2">
				<div class="container mx-auto max-w-5xl">
					<FormProgress />
				</div>
			</div>

			<div class="z-[4] order-3">
				<div
					v-if="needToShowForm"
					class="lg:text-main-50 bg-vinButtonBgMobile lg:bg-vinButtonBg fixed bottom-24 left-[50%] mx-auto mt-4 flex max-h-[50px] min-h-[50px] w-[80%] max-w-[85%] translate-x-[-50%] flex-col items-center overflow-hidden rounded-[12px] px-3 py-3 text-[12px] text-white transition-[min-height,background,max-height,max-width] delay-[rounded] duration-500 lg:relative lg:bottom-[auto] lg:left-[auto] lg:mt-6 lg:max-h-[60px] lg:w-[550px] lg:translate-x-0 lg:cursor-pointer lg:justify-start lg:rounded-[21px] lg:!px-0 lg:px-8 lg:py-4 lg:text-[18px]"
					:class="{
						'h-[auto] !max-h-[70vh]  !min-h-[30vh] w-[85%] max-w-[85%] !bg-white !p-8 shadow-lg':
							dialogs.headerFormModalOpen && !grid.lg,
						'animate-vin-button left-[auto] right-5 translate-x-[0] shadow-lg':
							!isButtonExpand && !grid.lg,
						'!bg-white lg:h-[auto] lg:max-h-[600px]': isFormVisible,
					}"
				>
					<div
						v-if="!dialogs.headerFormModalOpen && !dialogs.headerFormModalError"
						class="flex items-center justify-between lg:justify-center"
						@click="onButtonClick"
					>
						<div
							class="opacity-1 absolute top-[13px] left-[13px] transition duration-300 lg:top-[18px] lg:left-[18px]"
							:class="{ 'opacity-0': isFormVisible }"
						>
							<BarcodeSvg class="fill-vinButtonIconMobile lg:fill-vinButtonIcon" />
						</div>

						<p
							v-if="isButtonExpand || grid.lg"
							class="opacity-1 text-vinButtonTextMobile lg:text-vinButtonText mt-[5px] pl-2 text-center font-bold transition duration-300 lg:mt-0 lg:mr-4 lg:pl-10 lg:pl-6"
							:class="{ 'opacity-0': isFormVisible }"
						>
							{{
								grid.sm
									? 'Enter VIN or Plate for a Better Offer'
									: 'Enter VIN or Plate for Best Offer'
							}}
						</p>

						<div
							v-if="
								(!dialogs.headerFormModalOpen && !grid.lg) ||
								isButtonExpand ||
								grid.lg
							"
							class="flex items-center justify-center"
							@click.prevent="onCloseButtonClick"
						>
							<CloseIcon
								v-if="!grid.lg"
								class="fill-vinButtonIconMobile absolute right-3 mt-1 block h-4 w-4 transition-[0.3s] lg:top-4 lg:right-5 lg:mt-0 lg:h-7 lg:w-4 lg:rotate-[0]"
								:class="{
									hidden: !isButtonExpand && !grid.lg,
								}"
								alt="close"
							/>

							<ChevronDownSvg
								v-else
								class="fill-vinButtonIcon absolute right-2 mt-1 block h-3 w-3 transition-[0.3s] lg:top-[14px] lg:right-3 lg:mt-0 lg:h-8 lg:w-8 lg:rotate-[0]"
								:class="{
									'fill-vinButtonIconOpen lg:rotate-[180deg]': isFormVisible,
								}"
							/>
						</div>
					</div>

					<div v-if="needToShowForm && isFormVisible">
						<div class="mx-auto mt-0 max-w-[600px] rounded-[21px] lg:w-[500px]">
							<template v-if="dialogs.headerFormModalError">
								<div class="mb-8">
									<h3
										class="text-main-400 text-20 md:text-24 lg:text-28 mt-5 text-center font-bold"
									>
										{{ modalTitle }}
									</h3>
								</div>

								<div
									v-if="
										dialogs.errorModal.type === 'vin' &&
										!dialogs.errorModal.isOld
									"
									class="mb-8 lg:pl-3"
								>
									<div class="flex items-center">
										<div class="mr-3">
											<CheckSecondSvg class="fill-main-400 w-5 lg:w-6" />
										</div>
										<div class="text-primary text-base lg:text-xl">
											<p>A VIN cannot include letters I, O, Q</p>
										</div>
									</div>

									<div class="flex items-center">
										<div class="mr-3">
											<CheckSecondSvg class="fill-main-400 w-5 lg:w-6" />
										</div>
										<div class="text-primary text-base lg:text-xl">
											<p>A VIN is made up of 17 characters</p>
										</div>
									</div>
								</div>

								<div>
									<h4
										class="text-main-400 text-18 md:text-20 text-center font-bold"
									>
										{{ bottomText }}
									</h4>
								</div>

								<div class="mt-5 flex justify-center">
									<AppButton
										class="xs:basis-0 text-md xs:text-base min-w-[190px] basis-2/3"
										size="Normal"
										@click="dialogs.toggleFormModalError(false)"
									>
										<span>Ok</span>
									</AppButton>
								</div>
							</template>

							<template v-else-if="dialogs.trimsModalOpen && form.trimsModal.length">
								<div class="-mx-6">
									<Trims in-header />
								</div>
								<AppButton
									class="w-full"
									cta
									:disabled="!form.trimModal.body_type"
									@click="onContinueTrimClick"
								>
									<span class="z-2">Continue</span>
								</AppButton>
							</template>

							<div
								v-else
								class="hidden w-full overflow-hidden lg:block"
								:class="{ ' max-h-[600px]': isFormVisible }"
							>
								<MainPageForm
									ymm-hidden
									in-header
									@close-form="isFormVisible = false"
								/>
							</div>
						</div>
					</div>

					<div
						v-else
						class="hidden h-full w-full opacity-0 transition-[opacity]"
						:class="{
							'!block !opacity-[1] delay-300':
								dialogs.headerFormModalOpen && !grid.lg,
						}"
					>
						<div
							class="absolute right-4 top-4 z-[3] h-6 w-6 lg:cursor-pointer"
							@click="onCloseModalClick"
						>
							<CloseSvg class="fill-secondary" />
						</div>

						<div class="flex h-full items-center">
							<div v-if="dialogs.headerFormModalError">
								<div class="mb-8">
									<h3
										class="text-main-400 text-20 md:text-24 lg:text-28 mt-5 text-center font-bold"
									>
										{{ modalTitle }}
									</h3>
								</div>

								<div
									v-if="
										dialogs.errorModal.type === 'vin' &&
										!dialogs.errorModal.isOld
									"
									class="mb-8 lg:pl-3"
								>
									<div class="flex items-center">
										<div class="mr-3">
											<CheckSecondSvg class="fill-main-400 w-5 lg:w-6" />
										</div>
										<div class="text-primary text-base lg:text-xl">
											<p>A VIN cannot include letters I, O, Q</p>
										</div>
									</div>

									<div class="flex items-center">
										<div class="mr-3">
											<CheckSecondSvg class="fill-main-400 w-5 lg:w-6" />
										</div>
										<div class="text-primary text-base lg:text-xl">
											<p>A VIN is made up of 17 characters</p>
										</div>
									</div>
								</div>

								<div>
									<h4
										class="text-main-400 text-18 md:text-20 text-center font-bold"
									>
										{{ bottomText }}
									</h4>
								</div>

								<div class="mt-5 flex justify-center">
									<AppButton
										class="xs:basis-0 text-md xs:text-base min-w-[190px] basis-2/3"
										size="Normal"
										@click="dialogs.toggleFormModalError(false)"
									>
										<span>Ok</span>
									</AppButton>
								</div>
							</div>

							<div v-else class="h-full">
								<MainPageForm ymm-hidden in-header />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-if="isTooltipVisible">
			<HelpfulTipsSecond :text="'Why we ask'">
				Our quotes are not automated. We will carefully research your car to make a
				competitive quote. We ask for your info so we can send you a quote for your
				{{ form.make }} once it is ready. We will only contact you about your car. Opt out
				at any time.
			</HelpfulTipsSecond>
		</div>
	</section>
</template>

<script lang="ts" setup>
	import { Steps, useFormStore } from '../../../stores/form'
	import { useAppScreen } from '../../../services/app-screen'
	import CloseSvg from './icons/CloseSvg.vue'
	import { getAssetUrl } from '../../../services/helpers'
	import FormProgress from './FormProgress.vue'
	import HelpfulTipsSecond from './HelpfulTipsSecond.vue'
	import { computed, ref } from 'vue'
	import { useDialogStore } from '../../../stores/dialog'
	import LogoPower from '../../common/LogoPowerSvg.vue'
	import MainPageForm from '../MainPageForm/MainPageForm.vue'
	import AppButton from '../../common/AppButton.vue'
	import CheckSecondSvg from './icons/CheckSecondSvg.vue'
	import Trims from '../steps/Trims.vue'
	import closeImg from '../../../assets/icons/close.svg'
	import barcodeImg from '../../../assets/icons/barcode.svg'
	import chevroneImg from '../../../assets/icons/chevrone.svg'
	import ChevronDownSvg from './icons/ChevronDownSvg.vue'
	import BarcodeSvg from './icons/BarcodeSvg.vue'
	import CloseIcon from './icons/CloseIcon.vue'

	const form = useFormStore()
	const dialogs = useDialogStore()
	const { grid } = useAppScreen()

	const isFormVisible = ref(false)
	const isExpand = ref(true)

	const isWarningVisible = computed(() => {
		switch (form.currentStep.id) {
			case Steps.Rejected:
			case Steps.NeedChat: {
				return true
			}
			default:
				return false
		}
	})

	const isTooltipVisible = computed(() => {
		return form.currentStep.id === Steps.ContactInfo
	})

	const isSuccessScreen = computed(() => {
		return form.currentStep.id === Steps.Congrats
	})

	const modalTitle = computed(() => {
		return dialogs.errorModal.title
	})

	const bottomText = computed(() => {
		return dialogs.errorModal.subtitle
	})

	const isButtonExpand = computed(() => {
		if (dialogs.headerFormModalOpen) {
			return true
		}
		if (isExpand.value === false) {
			return false
		}
		return !(form.currentStep.id > 2)
	})

	const needToShowForm = computed(() => {
		return (
			!form.vinOrLPType &&
			!(
				form.currentStep.id === Steps.AboutYourRide ||
				form.currentStep.id === Steps.Congrats ||
				form.currentStep.id === Steps.ContactInfo
			)
		)
	})

	const onButtonClick = () => {
		grid.lg ? (isFormVisible.value = !isFormVisible.value) : dialogs.openHeaderFormModal()
	}

	const onCloseButtonClick = (e: MouseEvent) => {
		if (!grid.lg) {
			e.stopPropagation()
			// form.vinOrLPType = true
			isExpand.value = false
		}
	}

	const onCloseModalClick = (e: MouseEvent) => {
		e.stopPropagation()
		dialogs.closeHeaderFormModal()
		if (dialogs.headerFormModalError) {
			dialogs.toggleFormModalError(false)
		}
	}

	const onContinueTrimClick = () => {
		form.SubmitTrimsFormModal()
			.then(() => {
				form.trim = form.trimModal
				form.trims = form.trimsModal
				form.year = form.modalVehicleData.year
				form.make = form.modalVehicleData.make
				form.makes = form.modalVehicleData.makes
				form.models = form.modalVehicleData.models
				form.model = form.modalVehicleData.model
				form.vin = form.modalVehicleData.vin
				form.plate = form.modalVehicleData.plate
				form.state = form.modalVehicleData.state
				form.styles = form.modalVehicleData.styles
				form.vinOrLPType = true
			})
			.finally(() => {
				dialogs.closeTrimsModal()
			})
	}

	const headerFormTitle = () => {
		switch (form.currentStep.id) {
			case Steps.StandardQuiz:
				return `Verify the following are true for your ${form.make}`
			case Steps.SelectLocation:
				return `Where would you like to trade in your ${form.make}?`
			default:
				return form.currentStep.title
		}
	}
</script>
